import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Chart } from 'src/app/models/chart.model';
import { SimulationTabModel } from 'src/app/models/simulation-tab.model';
import { PixelService } from 'src/app/services/pixel.service';
import { EweService } from 'src/app/services/ewe.service';
import { forkJoin } from 'rxjs';
import * as moment from 'moment';

import * as Highcharts from 'highcharts';
import addMore from "highcharts/highcharts-more";
addMore(Highcharts);
import HC_timeLine from "highcharts/modules/timeline";
import { of } from 'rxjs';
import { stringify } from '@angular/compiler/src/util';
import { nextTick } from 'process';
HC_timeLine(Highcharts);

declare var require: any;

declare module 'highcharts' {
  interface Point {
    highlight: (event: Highcharts.PointerEventObject) => void;
  }
}

/**
 * Override the reset function, we don't need to hide the tooltips and
 * crosshairs.
 */
Highcharts.Pointer.prototype.reset = function () {
  return undefined;
};

/**
 * Highlight a point by showing tooltip, setting hover state and draw crosshair
 */
Highcharts.Point.prototype.highlight = function (event) {
  event = this.series.chart.pointer.normalize(event);
  this.onMouseOver(); // Show the hover marker
  // this.series.chart.tooltip.refresh(this); // Show the tooltip
  // this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
};

@Component({
  selector: 'app-simulation-results',
  templateUrl: './simulation-results.component.html',
  styleUrls: ['./simulation-results.component.scss']
})
export class SimulationResultsComponent implements OnInit, OnChanges {

  Highcharts: typeof Highcharts = Highcharts;

  public chartReady = false;

  @Input()
  public simulation: SimulationTabModel;

  public compareYears = [];

  public chartOptions: any;
  public chartOptionsAcc: any;
  public chartPhases: any;
  public chartLabels: any;

  public chartInstances: any = {
    avgT: null,
    totR: null,
    arid: null,
    ewe: null
  };

  public charts: Array<Chart>;
  public chartsAcc: Array<Chart>;

  constructor(private pixelService: PixelService, private eweService: EweService) {
    this.fillCompareYears();
  }

  ngOnChanges(): void {
    this.chartPhases = this.simulation.result.chartPhases;
    this.chartLabels = this.simulation.result.chartLabels;
    this.chartOptions = this.simulation.result.chartOptions;
    this.chartOptionsAcc = this.simulation.result.chartOptionsAcc;
    this.initChart();
  }

  ngOnInit(): void {
    if (this.simulation.status == 2) {
      // Load Season data
      this.downloadClimateData(this.simulation.coordinates.latitude, this.simulation.coordinates.longitude, this.chartPhases.xAxis[0].format("YYYYMMDD"), this.chartPhases.xAxis[1].format("YYYYMMDD"), Object.keys(this.chartOptions), moment(this.simulation.plantingDate, "YYYY-MM-DD").year(), 0);
      // Load Compare year data
      this.downloadClimateData(this.simulation.coordinates.latitude, this.simulation.coordinates.longitude, this.compareYearDates[0], this.compareYearDates[1], Object.keys(this.chartOptions), moment(this.simulation.plantingDate, "YYYY-MM-DD").year(), 1);
      // Load last 5 years average
      this.downloadClimateData(this.simulation.coordinates.latitude, this.simulation.coordinates.longitude, this.compareYearDates[0], this.compareYearDates[1], Object.keys(this.chartOptions), moment(this.simulation.plantingDate, "YYYY-MM-DD").year(), 2);
       
    } else {
      setTimeout(() => {
        Object.keys(this.chartInstances).filter(key => this.chartInstances[key] != null).forEach(key => {
          this.chartInstances[key].hideLoading();
        });
      }, 100);
    }
  }

  private get compareYearDates(): Array<string> {

    let sDate;
    let yDiff;
    let eDate;

    if(this.chartPhases.xAxis[0].format("DDD") > this.chartPhases.xAxis[1].format("DDD")){
      sDate = this.simulation.compareYear + this.chartPhases.xAxis[0].format("MMDD");
      yDiff = this.chartPhases.xAxis[0].diff(this.chartPhases.xAxis[1], 'years') + 1;
      eDate = (Number(this.simulation.compareYear) + yDiff) + this.chartPhases.xAxis[1].format("MMDD");
    }else{
      sDate = this.simulation.compareYear + this.chartPhases.xAxis[0].format("MMDD");
      yDiff = this.chartPhases.xAxis[0].diff(this.chartPhases.xAxis[1], 'years');
      eDate = (Number(this.simulation.compareYear) + yDiff) + this.chartPhases.xAxis[1].format("MMDD");
    }

    return [sDate, eDate];
  }

  private downloadClimateData(latitude: number, longitude: number, from: string, to: string, variables: Array<string>, year: number, chartSeries: number = 0): void { 
    this.simulation.status = 2;
    
    Object.keys(this.chartInstances).filter(key => this.chartInstances[key] != null).forEach(key => {
      this.chartInstances[key].showLoading('Loading...');
    });

    let variable;
    let threshold;

    switch(chartSeries){
      case 0:
        variable = "eweLt";
        threshold = "t0";
        break;
      case 1:
        variable = "eweHt";
        threshold = "t35";
        break;
      case 2:
        variable = "eweDp";
        threshold = "t10";
        break;
    }

    if (chartSeries == 2) {
      // Process last 5 years avg
      const startDateRange1 = moment(moment(from).subtract(1, 'year').format('YYYY') + '-' + moment(from).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDateRange1 = moment(moment(to).subtract(1, 'year').format('YYYY') + '-' + moment(to).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const data1 = this.pixelService.getPixelData(latitude, longitude, startDateRange1, endDateRange1, variables);

      const startDateRange2 = moment(moment(startDateRange1).subtract(1, 'year').format('YYYY') + '-' + moment(from).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDateRange2 = moment(endDateRange1).subtract(1, 'year').format('YYYY-MM-DD');
      const data2 = this.pixelService.getPixelData(latitude, longitude, startDateRange2, endDateRange2, variables);

      const startDateRange3 = moment(moment(startDateRange2).subtract(1, 'year').format('YYYY') + '-' + moment(from).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDateRange3 = moment(endDateRange2).subtract(1, 'year').format('YYYY-MM-DD');
      const data3 = this.pixelService.getPixelData(latitude, longitude, startDateRange3, endDateRange3, variables);

      const startDateRange4 = moment(moment(startDateRange3).subtract(1, 'year').format('YYYY') + '-' + moment(from).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDateRange4 = moment(endDateRange3).subtract(1, 'year').format('YYYY-MM-DD');
      const data4 = this.pixelService.getPixelData(latitude, longitude, startDateRange4, endDateRange4, variables);

      const startDateRange5 = moment(moment(startDateRange4).subtract(1, 'year').format('YYYY') + '-' + moment(from).format('MM-DD'), 'YYYY-MM-DD').format('YYYY-MM-DD');
      const endDateRange5 = moment(endDateRange4).subtract(1, 'year').format('YYYY-MM-DD');
      const data5 = this.pixelService.getPixelData(latitude, longitude, startDateRange5, endDateRange5, variables);

      const ewePromise = this.eweService.getEweData(latitude, longitude, this.chartPhases.xAxis[0].format("YYYY-MM-DD"), this.chartPhases.xAxis[1].format("YYYY-MM-DD"), variable, threshold);

      forkJoin([data1, data2, data3, data4, data5, ewePromise]).subscribe(res => {

        const arr5YearsAccAvg = [];

        this.chartOptions["ewe"].series[2].data = [];
        
        if(res[5].code == 'SUCCESS'){
          res[5].data.sort(function(a, b){
            return moment(a.date, "YYYY-MM-DD").valueOf() - moment(b.date, "YYYY-MM-DD").valueOf();
          }).forEach(d => {
            this.chartOptions["ewe"].series[2].data.push([
              moment(d.date, "YYYY-MM-DD").valueOf(),
              this.chartOptions["ewe"].dataFormatter(d[variable+'_'+threshold])
            ]);
          });
        }
        
        if( (res[0].code == 'SUCCESS') && (res[1].code == 'SUCCESS') && (res[2].code == 'SUCCESS') && (res[3].code == 'SUCCESS') && (res[4].code == 'SUCCESS')){
          
          try {
            for (let i = 0 ; i < res[0].data.length; i++) {
              const totR = res[0].data[i].totR + res[1].data[i].totR + res[2].data[i].totR + res[3].data[i].totR + res[4].data[i].totR;
              const arid = res[0].data[i].arid + res[1].data[i].arid + res[2].data[i].arid + res[3].data[i].arid + res[4].data[i].arid;
    
              arr5YearsAccAvg.push({
                totR: (totR / 5) / 25.4,
                arid: arid / 5,
                date: this.chartOptionsAcc['arid'].series[1].data[i][0]
              });        
            }
    
            arr5YearsAccAvg.reduce((acc, val) => {
              this.chartOptionsAcc['arid'].series[2].data.push([val.date, acc.arid + val.arid]);
              this.chartOptionsAcc['totR'].series[2].data.push([val.date, acc.totR + val.totR]);
              return { arid: acc.arid + val.arid, totR: acc.totR + val.totR };
            }, { arid: 0, totR: 0 });
            
          } catch (error) {
            console.error(error);
          }
        }

        this.updateChartsData(this.simulation.dataMode, [chartSeries]);

      });
      
    } else { //Chart series 0 and 1

      let pixelPromise = this.pixelService.getPixelData(latitude, longitude, from, to, variables);
      let ewePromise = this.eweService.getEweData(latitude, longitude, this.chartPhases.xAxis[0].format("YYYY-MM-DD"), this.chartPhases.xAxis[1].format("YYYY-MM-DD"), variable, threshold);

      forkJoin([pixelPromise, ewePromise]).subscribe(response => {
        let pixelResult = response[0];
        let eweResult = response[1];
        
        Object.keys(this.chartOptions).forEach(key => {
          this.chartOptions[key].series[chartSeries].data = [];

          if(key == "ewe"){
            
            if(eweResult.code == 'SUCCESS'){

              eweResult.data.sort(function(a, b){
                return moment(a.date, "YYYY-MM-DD").valueOf() - moment(b.date, "YYYY-MM-DD").valueOf();
              }).forEach(d => {
                this.chartOptions[key].series[chartSeries].data.push([
                  moment(d.date, "YYYY-MM-DD").valueOf(),
                  this.chartOptions[key].dataFormatter(d[variable+'_'+threshold])
                ]);
              });

            }else{
              console.log("Error downloading EWE data");
            }

          }else{
            
            if (pixelResult.code == 'SUCCESS') {

              pixelResult.data.forEach(d => {
                let varDate;
                if(moment(from, "YYYY-MM-DD").format("DDD") > moment(to, "YYYY-MM-DD").format("DDD")){
                  if(moment(d.date, "YYYY-MM-DD").format("YYYY") == moment(from, "YYYY-MM-DD").format("YYYY")){
                    varDate = moment(d.date, "YYYY-MM-DD").add(year - moment(d.date, "YYYY-MM-DD").year(), 'years').valueOf();
                  }else{
                    varDate = moment(d.date, "YYYY-MM-DD").add((year+1) - moment(d.date, "YYYY-MM-DD").year(), 'years').valueOf();
                  }
                }else{
                  varDate = moment(d.date, "YYYY-MM-DD").add(year - moment(d.date, "YYYY-MM-DD").year(), 'years').valueOf();
                }

                this.chartOptions[key].series[chartSeries].data.push([
                  varDate,
                  this.chartOptions[key].dataFormatter(d[key])
                ]);
              });
            
              let seriesData = { data: this.chartOptions[key].series[chartSeries].data };

              // Update label names if series == 1 (compare Year)
              if (chartSeries == 1) {
                this.chartOptions[key].series[1].name = this.simulation.compareYear;
                seriesData['name'] = this.simulation.compareYear
              }

              Object.keys(this.chartOptionsAcc).forEach(key => {
                this.chartOptionsAcc[key].series[chartSeries].data = [];
    
                this.chartOptions[key].series[chartSeries].data.reduce((acc, val) => {
                  this.chartOptionsAcc[key].series[chartSeries].data.push([val[0], acc + val[1]]);
                  return acc + val[1];
                }, 0);
    
                let seriesDataAcc = { data: this.chartOptionsAcc[key].series[chartSeries].data };
    
                // Update label names if series == 1 (compare Year)
                if (chartSeries == 1) {
                  this.chartOptionsAcc[key].series[1].name = this.simulation.compareYear;
                  seriesDataAcc['name'] = this.simulation.compareYear
                }
    
              });

            }else{
              console.log("Error downloading PIXEL data");
            }
          }
  
          this.updateChartsData(this.simulation.dataMode, [chartSeries]);
          
        });
      });
    }
  }

  private initChart(): void {
    this.charts = [];
    this.chartsAcc = [];

    Object.keys(this.chartOptions).forEach(key => {

      this.charts.push({
        options: {
          chart: {
            spacingTop: 10,
            marginLeft: 90,
            height: 200,
            className: "chart-sync-a",
            animation: false
          },

          title: {
            text: null
          },

          credits: {
            enabled: false
          },

          legend: {
            enabled: false
          },

          tooltip: {
            animation: false,
            enabled: true,
            shared: true,
            fontSize: '1.2em',
            useHTML: true,
            snap: true,
            backgroundColor: '#FFF',
            xDateFormat: '%m/%d/%Y',
            headerFormat: '<div style="font-size: 1.1em;"><b>{point.key}</b><div>'
          },

          xAxis: {
            type: 'datetime',

            min: this.chartPhases.xAxis[0].valueOf(),
            max: this.chartPhases.xAxis[1].valueOf(),

            labels: {
              style: {
                fontSize: '1em'
              }
            },

            title: {},

            crosshair: true,
            events: {
              setExtremes: function (e) {
                var thisChart = this.chart;

                if (e.trigger !== "syncExtremes") {
                  // Prevent feedback loop
                  Highcharts.each(Highcharts.charts, function (chart) {
                    if (
                      chart !== thisChart &&
                      chart.options.chart.className ===
                      thisChart.options.chart.className
                    ) {
                      if (chart.xAxis[0].setExtremes) {
                        // It is null while updating
                        chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
                          trigger: "syncExtremes"
                        });
                      }
                    }
                  });
                }
              }
            },

            plotLines: [
              // Phases
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[0].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[1].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[2].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },

              // Phases changing window
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[0].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[1].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[2].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[3].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[4].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[5].valueOf()
              }
            ],

            plotBands: [
              // Phases
              {
                from: this.chartPhases.bands[0][0].valueOf(),
                to: this.chartPhases.bands[0][1].valueOf(),
                color: 'rgba(240,201,163, .15)',
                label: {
                  text: this.chartLabels.band[0].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#B09377',
                  },
                  y: 50
                }
              },
              {
                from: this.chartPhases.bands[1][0].valueOf(),
                to: this.chartPhases.bands[1][1].valueOf(),
                color: 'rgba(181,209,119, .15)',
                label: {
                  text: this.chartLabels.band[1].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#74894C',
                  },
                  y: 50
                }
              },
              {
                from: this.chartPhases.bands[2][0].valueOf(),
                to: this.chartPhases.bands[2][1].valueOf(),
                color: 'rgba(93,171,91, .15)',
                label: {
                  text: this.chartLabels.band[2].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#4D7039',
                  },
                  y: 50
                }
              },

              // Phase changing window
              {
                from: this.chartPhases.bandsWindow[0][0].valueOf(),
                to: this.chartPhases.bandsWindow[0][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[0] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              },
              {
                from: this.chartPhases.bandsWindow[1][0].valueOf(),
                to: this.chartPhases.bandsWindow[1][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[1] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              },
              {
                from: this.chartPhases.bandsWindow[2][0].valueOf(),
                to: this.chartPhases.bandsWindow[2][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[2] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              }
            ]
          },

          plotOptions: {
            series: {
              animation: false,
              marker: {
                enabled: false
              },
              states: {
                inactive: {
                  opacity: 1,
                },
                hover: { enabled: false }
              }
            }
          },

          yAxis: {
            gridLineWidth: 1,
            minorGridLineWidth: 0,

            max: this.chartOptions[key].yMax,
            min: this.chartOptions[key].yMin,

            labels: {
              format: '{value} ' + this.chartOptions[key].unit,
              align: 'right',
              style: {
                color: this.chartOptions[key].color,
                fontSize: '1em'
              }
            },
            title: {
              enabled: true,
              text: this.chartOptions[key].title,
              style: {
                color: this.chartOptions[key].color,
                fontSize: '1.1em'
              }
            }
          },

          series: [
            {
              color: this.chartOptions[key].series[0].color,
              data: this.chartOptions[key].series[0].data || [],
              name: this.chartOptions[key].series[0].name,
              type: this.chartOptions[key].series[0].type,
              
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[0].pointFormat + ' ' + this.chartOptions[key].series[0].unit,
              },
              zIndex: 2,
              lineWidth: 3
            },
            {
              color: this.chartOptions[key].series[1].color,
              data: this.chartOptions[key].series[1].data || [],
              name: this.chartOptions[key].series[1].name,
              type: this.chartOptions[key].series[1].type,
              // dashStyle: 'shortDot',
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[1].pointFormat + ' ' + this.chartOptions[key].series[1].unit,
              },
              zIndex: 1,
              lineWidth: 2
            },
            {
              color: this.chartOptions[key].series[2].color,
              data: this.chartOptions[key].series[2].data || [],
              name: this.chartOptions[key].series[2].name,
              type: this.chartOptions[key].series[2].type,
              // dashStyle: 'shortDot',
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[2].pointFormat + ' ' + this.chartOptions[key].series[2].unit,
              },
              zIndex: 1,
              lineWidth: 2
            }
          ]
        }
      });
    });
  
    Object.keys(this.chartOptionsAcc).forEach(key => {
      this.chartsAcc.push({
        options: {
          chart: {
            spacingTop: 10,
            marginLeft: 90,
            height: 200,
            className: "chart-sync-a",
            animation: false
          },

          title: {
            text: null
          },

          credits: {
            enabled: false
          },

          legend: {
            enabled: false
          },

          tooltip: {
            animation: false,
            enabled: true,
            shared: true,
            fontSize: '1.2em',
            useHTML: true,
            snap: true,
            backgroundColor: '#FFF',
            xDateFormat: '%m/%d/%Y',
            headerFormat: '<div style="font-size: 1.1em;"><b>{point.key}</b><div>'
          },

          xAxis: {
            type: 'datetime',

            min: this.chartPhases.xAxis[0].valueOf(),
            max: this.chartPhases.xAxis[1].valueOf(),

            labels: {
              style: {
                fontSize: '1em'
              }
            },

            title: {},

            crosshair: true,
            events: {
              setExtremes: function (e) {
                var thisChart = this.chart;

                if (e.trigger !== "syncExtremes") {
                  // Prevent feedback loop
                  Highcharts.each(Highcharts.charts, function (chart) {
                    if (
                      chart !== thisChart &&
                      chart.options.chart.className ===
                      thisChart.options.chart.className
                    ) {
                      if (chart.xAxis[0].setExtremes) {
                        // It is null while updating
                        chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
                          trigger: "syncExtremes"
                        });
                      }
                    }
                  });
                }
              }
            },

            plotLines: [
              // Phases
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[0].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[1].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },
              {
                color: '#333',
                dashStyle: 'solid',
                width: 1,
                value: this.chartPhases.lines[2].valueOf(),
                label: {
                  text: moment(this.chartPhases.xAxis[0], 'YYYY-MM-DD').year(),
                  style: {
                    color: '#333'
                  },
                  rotation: -90,
                  textAlign: 'right',
                  x: -4
                }
              },

              // Phases changing window
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[0].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[1].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[2].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[3].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[4].valueOf()
              },
              {
                color: '#AAA',
                dashStyle: 'dash',
                value: this.chartPhases.linesWindow[5].valueOf()
              }
            ],

            plotBands: [
              // Phases
              {
                from: this.chartPhases.bands[0][0].valueOf(),
                to: this.chartPhases.bands[0][1].valueOf(),
                color: 'rgba(240,201,163, .15)',
                label: {
                  text: this.chartLabels.band[0].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#B09377',
                  },
                  y: 50
                }
              },
              {
                from: this.chartPhases.bands[1][0].valueOf(),
                to: this.chartPhases.bands[1][1].valueOf(),
                color: 'rgba(181,209,119, .15)',
                label: {
                  text: this.chartLabels.band[1].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#74894C',
                  },
                  y: 50
                }
              },
              {
                from: this.chartPhases.bands[2][0].valueOf(),
                to: this.chartPhases.bands[2][1].valueOf(),
                color: 'rgba(93,171,91, .15)',
                label: {
                  text: this.chartLabels.band[2].toUpperCase(),
                  style: {
                    fontSize: '1.2em',
                    fontWeight: 'bold',
                    color: '#4D7039',
                  },
                  y: 50
                }
              },

              // Phase changing window
              {
                from: this.chartPhases.bandsWindow[0][0].valueOf(),
                to: this.chartPhases.bandsWindow[0][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[0] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              },
              {
                from: this.chartPhases.bandsWindow[1][0].valueOf(),
                to: this.chartPhases.bandsWindow[1][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[1] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              },
              {
                from: this.chartPhases.bandsWindow[2][0].valueOf(),
                to: this.chartPhases.bandsWindow[2][1].valueOf(),
                color: 'rgba(0,0,0, .02)',
                label: {
                  text: this.chartLabels.window[2] + '<br> window',
                  style: {
                    fontSize: '1em',
                    fontWeight: 'normal',
                    color: '#999',
                  },
                  y: 130
                }
              }
            ]
          },

          plotOptions: {
            series: {
              animation: false,
              marker: {
                enabled: false
              },
              states: {
                inactive: {
                  opacity: 1,
                },
                hover: { enabled: false }
              }
            }
          },

          yAxis: {
            gridLineWidth: 1,
            minorGridLineWidth: 0,

            labels: {
              format: '{value} ' + this.chartOptionsAcc[key].unit,
              align: 'right',
              style: {
                color: this.chartOptionsAcc[key].color,
                fontSize: '1em'
              }
            },
            title: {
              enabled: true,
              text: this.chartOptionsAcc[key].title,
              style: {
                color: this.chartOptionsAcc[key].color,
                fontSize: '1.1em'
              }
            }
          },

          series: [
            {
              color: this.chartOptionsAcc[key].series[0].color,
              data: this.chartOptionsAcc[key].series[0].data || [],
              name: this.chartOptionsAcc[key].series[0].name,
              type: this.chartOptionsAcc[key].series[0].type,
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[0].pointFormat + ' ' + this.chartOptionsAcc[key].series[0].unit,
              },
              zIndex: 2,
              lineWidth: 3
            },
            {

              color: '#008080',
              data: this.chartOptionsAcc[key].series[1].data || [],
              name: this.chartOptionsAcc[key].series[1].name,
              type: this.chartOptionsAcc[key].series[1].type,
              // dashStyle: 'shortDot',
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[1].pointFormat + ' ' + this.chartOptionsAcc[key].series[1].unit,
              },
              zIndex: 1,
              lineWidth: 2
            },
            {

              color: '#999', // this.chartOptionsAcc[key].series[2].color,
              data: this.chartOptionsAcc[key].series[2].data || [],
              name: this.chartOptionsAcc[key].series[2].name,
              type: this.chartOptionsAcc[key].series[2].type,
              // dashStyle: 'shortDot',
              tooltip: {
                pointFormat: '<br/><span style="color:{series.color}">{series.name}: </span>' + this.chartOptions[key].series[2].pointFormat + ' ' + this.chartOptionsAcc[key].series[2].unit,
              },
              zIndex: 1,
              lineWidth: 2
            }
          ]
        }
      });
    });

    const that = this;
    setTimeout(() => {
      that.chartReady = true;
    })
  }

  private fillCompareYears(): void {
    for (let i = new Date().getFullYear()-1; i >= 1982; i--) {
      this.compareYears.push(i);
    }
  }

  public changeCompareYear(year: number): void {
    this.downloadClimateData(this.simulation.coordinates.latitude, this.simulation.coordinates.longitude, this.compareYearDates[0], this.compareYearDates[1], Object.keys(this.chartOptions), moment(this.simulation.plantingDate, "YYYY-MM-DD").year(), 1);
  }

  public changeDataMode(dataMode: string): void {
    this.chartInstances = [];
    this.updateChartsData(dataMode);
  }

  private updateChartsData(dataMode: string, chartSeries: Array<number> = [0, 1, 2]): void {

    let chartOptions;
    
    if (dataMode == 'daily'){
      chartOptions = this.chartOptions;
    }else if(dataMode == 'accumulated'){
      chartOptions = this.chartOptionsAcc;
    }else if(dataMode == 'ewe'){
      chartOptions = this.chartOptions;
    }

    setTimeout(() => {
      chartSeries.forEach(serie => {

        Object.keys(chartOptions).forEach(key => {
          if (this.chartInstances[key] != null && this.chartInstances[key].series != null && this.chartInstances[key].series[serie] != null) {
            this.chartInstances[key].series[serie].update({ name: chartOptions[key].series[serie].name, data: chartOptions[key].series[serie].data });

            // DUMB preloader hiding.. 2 requests one condition.. giving some time to load both requests
            setTimeout(() => {
              this.chartInstances[key].hideLoading();
            }, 1000);
          }
        });
      });
      setTimeout(() => {
        this.simulation.status = 3;
      }, 1000);
    });
  }

  public onChartLoaded(instance: any, name: string): void {
    this.chartInstances[name] = instance;
    this.chartInstances[name].showLoading('Loading...');
  }

  // Highcharts synchronization
  public hideTooltips = e => {
    var chart,
      point,
      i,
      event,
      charts = Highcharts.charts,
      sourceChart = charts[(Highcharts as any).hoverChartIndex];

    for (i = 0; i < charts.length; i = i + 1) {
      chart = charts[i];

      if (
        // Could be undefined for a removed chart
        chart
        // Only sync charts with shared class
        // chart.options.chart.className === sourceChart.options.chart.className
      ) {

        // Find coordinates within the chart
        event = chart.pointer.normalize(e);

        // Get the hovered point
        point = chart.series[1].searchPoint(event, true);

        if (point) {
          point.onMouseOut();
          chart.tooltip.hide(point);
          chart.xAxis[0].hideCrosshair();
        }
      }
    }

  };

  public synchronizeTooltips = e => {
    var chart,
      point,
      i,
      event,
      charts = Highcharts.charts;
    // sourceChart = charts[(Highcharts as any).hoverChartIndex];

    for (i = 0; i < charts.length; i = i + 1) {
      chart = charts[i];

      if (
        // Could be undefined for a removed chart
        chart
        // Only sync charts with shared class
        // chart.options.chart.className === sourceChart.options.chart.className
      ) {
        //       console.log(1)
        // Find coordinates within the chart
        event = chart.pointer.normalize(e);

        // Get the hovered point
        point = chart.series[1].searchPoint(event, true);

        if (point) {
          point.highlight(e);
        }
      }
    }
  };

}
